import { useCallback, useEffect, useState } from 'react';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type { DatabaseInfo } from '../utils/localizeContent';
import { localizeContent, tableMap } from '../utils/localizeContent';

export type { DatabaseInfo };

type Options = {
  targetLangText?: string | null;
  localizeByDefault?: boolean;
  loadOnMount?: boolean;
};

const useLocalizedText = (
  originalText?: string,
  originalLanguage?: LanguageRegionCode,
  targetLanguage?: LanguageRegionCode,
  dbInfo = {} as DatabaseInfo,
  options = {
    targetLangText: null,
    localizeByDefault: false,
    loadOnMount: false
  } as Options
) => {
  const [localizedText, setLocalizedText] = useState<string | null>(options.targetLangText || null);
  const canBeLocalized = originalText && originalLanguage && targetLanguage && originalLanguage !== targetLanguage;
  const [isLocalized, setIsLocalized] = useState(!!canBeLocalized && !!options.localizeByDefault);
  const [isLoading, setIsLoading] = useState(false);
  const tableName = tableMap[dbInfo.table];

  if (!tableName) {
    throw new Error('Table must be one of: activities, lists, reviews, trails, waypoints');
  }

  const loadLocalizedData = useCallback(async () => {
    if (localizedText || !canBeLocalized) return;
    setIsLoading(true);

    try {
      const data = await localizeContent(tableName, dbInfo.column, dbInfo.row);
      setLocalizedText(data[0]?.content);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }, [canBeLocalized, dbInfo.column, dbInfo.row, localizedText, tableName]);

  useEffect(() => {
    if (options.localizeByDefault || options.loadOnMount) loadLocalizedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleText = async () => {
    if (!canBeLocalized) return;
    if (!localizedText) await loadLocalizedData();
    setIsLocalized(!isLocalized);
  };

  return {
    canBeLocalized,
    isLoading,
    isLocalized,
    text: isLocalized ? localizedText : originalText,
    toggleText
  };
};

export default useLocalizedText;
